import React from "react";
import {
    Box,
    FormControl,
    FormControlLabel,
    IconButton,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Typography
} from "@mui/material";
import Cancel from "../../../../../public/assets/icons/cancel.svg";
import ChevronDown from "../../../../../public/assets/icons/chevron-down.svg";
import theme from "../../../../theme/Theme";
import { OptionEntry } from "../../../../types/cqa";
import MultiSelectAutocomplete from "../../../atoms/autocomplete/multiselectautocomplete";
import { LABEL_CODED_CASES, LABEL_IMPORTED_DATA, LABEL_INCOMING_CASES } from "../constants";
import "../ruleCommonSection.css";
import { ASTERISK_ELEMENT, CqaFormTextInput, DROPDOWN_STYLE, PLACEHOLDER_STYLE, SELECTION_COLOR } from "../ruleCommonStyleBlocks";

export const getDropdown = (
    setValues: (valueName: string, value: OptionEntry) => void,
    setMultiValue: (valueName: string, value: OptionEntry[]) => void,
    optionsMap: object,
    valuesMap: object,
    field,
    label,
    clearable = false,
    required = true,
    selectMultiple = false
) => {
    let value = valuesMap[field]?.label ?? ""

    if (selectMultiple) {
        value = valuesMap[field] ?? []
    }

    return (
        <Box display="flex" flexDirection="column" sx={{ width: "160px", gap: "4px" }}>
            <Typography className="setting_section_input_label">{label} {required ? ASTERISK_ELEMENT : ""}</Typography>
            <FormControl sx={{ width: "160px" }}>
                {!selectMultiple ? <Select
                    id={`${field}_select`}
                    displayEmpty
                    value={value}
                    onChange={(e) => setValues(field, e.target.value)}
                    IconComponent={() => (
                        <>
                            {clearable && (
                                <IconButton
                                    disableRipple
                                    style={{ padding: 0 }}
                                    onClick={() => {
                                        setValues(field, { label: "", value: undefined });
                                    }}
                                >
                                    <img src={Cancel} alt="clear-input" />
                                </IconButton>
                            )}
                            <IconButton disableRipple>
                                <img src={ChevronDown} alt="chevron-down" />
                            </IconButton>
                        </>
                    )}
                    sx={DROPDOWN_STYLE}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                "& .MuiList-root": {
                                    width: "100%",
                                },
                            }
                        },
                    }}
                    renderValue={(_) => {
                        if (!valuesMap[field]?.label) {
                            return (
                                <div
                                    style={{ color: theme.palette.artisticTextLow.main }}
                                >
                                    please select
                                </div>
                            );
                        }

                        return <div>{valuesMap[field]?.label}</div>;
                    }}
                >
                    {optionsMap[field.toLowerCase()]?.map((option) => (
                        <MenuItem
                            key={option?.value}
                            value={option}
                            sx={{ padding: "8px 16px" }}
                        >
                            {option?.label}
                        </MenuItem>
                    ))}
                </Select> : (
                    <MultiSelectAutocomplete
                        options={optionsMap[field.toLowerCase()]}
                        selectedValues={value}
                        onChange={(value) => setMultiValue(field, value)}
                        useCheckboxRender={true}
                    />
                )}
            </FormControl>
        </Box>
    );
}

interface Props {
    isCqaSample?: boolean;
    optionsMap: object;
    valuesMap: object;
    setMultiValue: (valueName: string, value: OptionEntry[]) => void;
    setValues: (valueName: string, value: OptionEntry) => void;
}

export const DataSource = ({
    isCqaSample = false,
    optionsMap = {},
    valuesMap,
    setMultiValue = () => { },
    setValues = () => { },
}: Props): JSX.Element => {
    return (
        <Box>
            {/* Container Title */}
            <Box className="setting_section_title_container">
                <Box className="setting_section_text_container" style={{ width: "92px" }}>
                    <span className="setting_section_text">
                        Data Source
                    </span>
                </Box>
            </Box>
            {/* main area */}
            <Box
                id="tasks-config"
                className='setting_section_main_container'
                sx={{ border: `1px solid ${theme.palette.artisticStroke200.main}`, minHeight: "260px" }}
            >
                {/* data source */}
                {
                    isCqaSample && (
                        <Box>
                            <Box className="section_container">
                                <Typography className="setting_section_input_label">
                                    Data Source {ASTERISK_ELEMENT}
                                </Typography>
                            </Box>
                            <FormControl>
                                <RadioGroup
                                    data-testid="dataSourceRadioGroup"
                                    onChange={() => { }}
                                    name="dataSourceRadioGroup"
                                    row
                                    sx={{ gap: "24px" }}
                                    value={LABEL_IMPORTED_DATA}
                                >
                                    <FormControlLabel
                                        data-testid="radioLabelGroup"
                                        disabled={true}
                                        value={LABEL_INCOMING_CASES}
                                        control={<Radio sx={SELECTION_COLOR} />}
                                        label="Incoming Cases"
                                    />
                                    <FormControlLabel
                                        data-testid="radioLabelIndividual"
                                        disabled={true}
                                        value={LABEL_CODED_CASES}
                                        control={<Radio sx={SELECTION_COLOR} />}
                                        label="Coded Cases"
                                    />
                                    <FormControlLabel
                                        data-testid="radioLabelIndividual"
                                        disabled={true}
                                        value={LABEL_IMPORTED_DATA}
                                        control={<Radio sx={SELECTION_COLOR} />}
                                        label="Imported Data"
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Box>
                    )
                }
                {/* unit of measure and volume */}
                <Box sx={{ display: "flex", flexDirection: "row", gap: "24px" }}>
                    {getDropdown(setValues, setMultiValue, optionsMap, valuesMap, "unit_of_measure", "Unit of Measure")}
                    <Box display="flex" flexDirection="column" sx={{ width: "160px", gap: "4px" }}>
                        <Typography className="setting_section_input_label">Volume {ASTERISK_ELEMENT}</Typography>
                        <CqaFormTextInput
                            data-testid="volume-input"
                            id="volume-input"
                            type="number"
                            placeholder="volume"
                            value={valuesMap["volume"]?.label}
                            onChange={(event) => {
                                if (parseInt(event.target.value)) {
                                    const newVal = {
                                        label: event.target.value,
                                        value: event.target.value
                                    }
                                    setValues("volume", newVal);
                                } else {
                                    setValues("volume", { label: "", value: undefined })
                                }
                            }}
                            sx={{
                                ...PLACEHOLDER_STYLE,
                                height: "32px",
                            }}
                        />
                    </Box>
                </Box>
                {/* predictability and frequency */}
                <Box sx={{ display: "flex", flexDirection: "row", gap: "24px" }}>
                    {getDropdown(setValues, setMultiValue, optionsMap, valuesMap, "predictability", "Predictability", false, true)}
                    {getDropdown(setValues, setMultiValue, optionsMap, valuesMap, "frequency", "How Often", true, false)}
                </Box>
            </Box >

        </Box>
    );
}